/**
 * IndexedDB Queue Utility
 * Stores failed operations (trades, messages, crimes) for retry when connection restores.
 * Enhanced with exponential backoff, conflict resolution, deduplication, and error logging.
 */

const DB_NAME = 'empirex-queue';
const DB_VERSION = 2;
const STORES = {
  pendingTrades: 'pendingTrades',
  pendingMessages: 'pendingMessages',
  pendingCrimes: 'pendingCrimes',
  errorLog: 'errorLog',
};

let dbInstance = null;

export async function openDB() {
  if (dbInstance) return dbInstance;
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORES.pendingTrades)) {
        const tradeStore = db.createObjectStore(STORES.pendingTrades, { keyPath: 'id', autoIncrement: true });
        tradeStore.createIndex('timestamp', 'timestamp');
      }
      if (!db.objectStoreNames.contains(STORES.pendingMessages)) {
        const msgStore = db.createObjectStore(STORES.pendingMessages, { keyPath: 'id', autoIncrement: true });
        msgStore.createIndex('timestamp', 'timestamp');
      }
      if (!db.objectStoreNames.contains(STORES.pendingCrimes)) {
        const crimeStore = db.createObjectStore(STORES.pendingCrimes, { keyPath: 'id', autoIncrement: true });
        crimeStore.createIndex('timestamp', 'timestamp');
      }
      if (!db.objectStoreNames.contains(STORES.errorLog)) {
        const errStore = db.createObjectStore(STORES.errorLog, { keyPath: 'id', autoIncrement: true });
        errStore.createIndex('timestamp', 'timestamp');
      }
    };
    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };
    request.onerror = () => reject(request.error);
  });
}

async function addToStore(storeName, item) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.add({ ...item, timestamp: Date.now(), retry_count: 0 });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getAllFromStore(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

async function removeFromStore(storeName, id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function updateItemInStore(storeName, id, updates) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const item = getReq.result;
      if (item) {
        const updated = { ...item, ...updates };
        const putReq = store.put(updated);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      } else {
        resolve();
      }
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

async function updateRetryCount(storeName, id, retryCount) {
  return updateItemInStore(storeName, id, { retry_count: retryCount });
}

async function logError(operation) {
  try {
    await addToStore(STORES?.errorLog, operation);
  } catch (e) {
    console.error('[Queue] Failed to log error:', e);
  }
}

// Exponential backoff delay: 1s, 2s, 4s, 8s, 16s max
const getBackoffDelay = (retryCount) => Math.min(1000 * Math.pow(2, retryCount), 16000);

// ── Queue Status Event Emitter ────────────────────────────────
const queueListeners = new Set();

export function onQueueStatusChange(listener) {
  queueListeners?.add(listener);
  return () => queueListeners?.delete(listener);
}

async function emitQueueStatus() {
  const count = await getPendingCount();
  queueListeners?.forEach((fn) => fn(count));
}

// ── Public API ──────────────────────────────────────────────

export async function queueTrade(trade) {
  try {
    await addToStore(STORES?.pendingTrades, {
      stock_id: trade?.stock_id,
      quantity: trade?.quantity,
      order_type: trade?.order_type,
      price: trade?.price,
      queued_price: trade?.price, // store original price for conflict detection
    });
    console.log('[Queue] Trade queued for retry');
    await emitQueueStatus();
  } catch (err) {
    console.error('[Queue] Failed to queue trade:', err);
  }
}

export async function queueMessage(message) {
  try {
    // Deduplication: check for same faction_id + message_text within last 5 seconds
    const existing = await getAllFromStore(STORES?.pendingMessages);
    const now = Date.now();
    const isDuplicate = existing?.some(
      (m) =>
        m?.faction_id === message?.faction_id &&
        m?.message_text === message?.message_text &&
        now - m?.timestamp < 5000
    );
    if (isDuplicate) {
      console.log('[Queue] Duplicate message detected, skipping queue');
      return;
    }
    await addToStore(STORES?.pendingMessages, {
      faction_id: message?.faction_id,
      message_text: message?.message_text,
    });
    console.log('[Queue] Message queued for retry');
    await emitQueueStatus();
  } catch (err) {
    console.error('[Queue] Failed to queue message:', err);
  }
}

export async function queueCrime(crime) {
  try {
    await addToStore(STORES?.pendingCrimes, {
      crime_id: crime?.crime_id,
      crime_key: crime?.crime_key,
    });
    console.log('[Queue] Crime queued for retry');
    await emitQueueStatus();
  } catch (err) {
    console.error('[Queue] Failed to queue crime:', err);
  }
}

export async function getPendingCount() {
  try {
    const [trades, messages, crimes] = await Promise.all([
      getAllFromStore(STORES?.pendingTrades),
      getAllFromStore(STORES?.pendingMessages),
      getAllFromStore(STORES?.pendingCrimes),
    ]);
    return trades?.length + messages?.length + crimes?.length;
  } catch {
    return 0;
  }
}

export async function getErrorLog() {
  try {
    return await getAllFromStore(STORES?.errorLog);
  } catch {
    return [];
  }
}

export async function clearErrorLog() {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORES.errorLog, 'readwrite');
    tx.objectStore(STORES.errorLog).clear();
    tx.oncomplete = () => resolve();
  });
}

const MAX_RETRIES = 5;

export async function processPendingQueue({ supabase, userId, onProgress, onConflict }) {
  if (!navigator.onLine) return { processed: 0, failed: 0, conflicts: [] };

  let processed = 0;
  let failed = 0;
  const conflicts = [];
  const failedOps = [];

  // ── Process pending trades ──────────────────────────────────
  try {
    const trades = await getAllFromStore(STORES?.pendingTrades);
    for (const trade of trades) {
      if (trade?.retry_count >= MAX_RETRIES) {
        await logError({ type: 'trade', payload: trade, reason: 'Max retries exceeded', timestamp: Date.now() });
        await removeFromStore(STORES?.pendingTrades, trade?.id);
        failedOps?.push({ type: 'trade', id: trade?.id, payload: trade, reason: 'Max retries exceeded' });
        continue;
      }

      // Exponential backoff wait
      if (trade?.retry_count > 0) {
        await new Promise((r) => setTimeout(r, getBackoffDelay(trade.retry_count - 1)));
      }

      try {
        // Conflict resolution: check if price moved > 5% since queue time
        if (trade?.queued_price && trade?.stock_id) {
          const { data: stockData } = await supabase
            ?.from('stocks')
            ?.select('current_price')
            ?.eq('id', trade?.stock_id)
            ?.single();
          if (stockData?.current_price) {
            const currentPrice = parseFloat(stockData?.current_price);
            const queuedPrice = parseFloat(trade?.queued_price);
            const priceDiff = Math.abs((currentPrice - queuedPrice) / queuedPrice);
            if (priceDiff > 0.05) {
              // Price moved > 5%, flag as conflict
              conflicts?.push({ trade, currentPrice, queuedPrice, priceDiff });
              onConflict?.({ trade, currentPrice, queuedPrice, priceDiff });
              await removeFromStore(STORES?.pendingTrades, trade?.id);
              continue;
            }
          }
        }

        const { error } = await supabase?.rpc('place_market_order', {
          p_user_id: userId,
          p_stock_id: trade?.stock_id,
          p_order_type: trade?.order_type,
          p_quantity: trade?.quantity,
          p_price: trade?.price,
        });
        if (error) throw error;
        await removeFromStore(STORES?.pendingTrades, trade?.id);
        processed++;
        onProgress?.({ type: 'trade', status: 'success' });
      } catch (err) {
        await updateRetryCount(STORES?.pendingTrades, trade?.id, trade?.retry_count + 1);
        failed++;
        if (trade?.retry_count + 1 >= MAX_RETRIES) {
          failedOps?.push({ type: 'trade', id: trade?.id, payload: trade, reason: err?.message });
        }
      }
    }
  } catch (err) {
    console.error('[Queue] Trade processing error:', err);
  }

  // ── Process pending messages ────────────────────────────────
  try {
    const messages = await getAllFromStore(STORES?.pendingMessages);
    for (const msg of messages) {
      if (msg?.retry_count >= MAX_RETRIES) {
        await logError({ type: 'message', payload: msg, reason: 'Max retries exceeded', timestamp: Date.now() });
        await removeFromStore(STORES?.pendingMessages, msg?.id);
        failedOps?.push({ type: 'message', id: msg?.id, payload: msg, reason: 'Max retries exceeded' });
        continue;
      }

      if (msg?.retry_count > 0) {
        await new Promise((r) => setTimeout(r, getBackoffDelay(msg.retry_count - 1)));
      }

      try {
        // Duplicate detection: check if same message was already sent
        const { data: existingMsgs } = await supabase
          ?.from('faction_messages')
          ?.select('id, created_at')
          ?.eq('faction_id', msg?.faction_id)
          ?.eq('message_text', msg?.message_text)
          ?.gte('created_at', new Date(msg.timestamp - 10000)?.toISOString())
          ?.limit(1);

        if (existingMsgs?.length > 0) {
          console.log('[Queue] Duplicate message detected on server, removing from queue');
          await removeFromStore(STORES?.pendingMessages, msg?.id);
          processed++;
          continue;
        }

        const { error } = await supabase?.rpc('send_faction_message', {
          p_faction_id: msg?.faction_id,
          p_message_text: msg?.message_text,
        })?.catch(() => ({
          // Fallback to direct insert if RPC doesn't exist
          error: null,
          _fallback: true,
        }));

        // If RPC not available, use direct insert
        if (!error) {
          const { error: insertErr } = await supabase?.from('faction_messages')?.insert({
            faction_id: msg?.faction_id,
            sender_id: userId,
            message_text: msg?.message_text,
            is_deleted: false,
            is_announcement: false,
          });
          if (insertErr) throw insertErr;
        } else {
          throw error;
        }

        await removeFromStore(STORES?.pendingMessages, msg?.id);
        processed++;
        onProgress?.({ type: 'message', status: 'success' });
      } catch (err) {
        await updateRetryCount(STORES?.pendingMessages, msg?.id, msg?.retry_count + 1);
        failed++;
        if (msg?.retry_count + 1 >= MAX_RETRIES) {
          failedOps?.push({ type: 'message', id: msg?.id, payload: msg, reason: err?.message });
        }
      }
    }
  } catch (err) {
    console.error('[Queue] Message processing error:', err);
  }

  // ── Process pending crimes ──────────────────────────────────
  try {
    const crimes = await getAllFromStore(STORES?.pendingCrimes);
    for (const crime of crimes) {
      if (crime?.retry_count >= MAX_RETRIES) {
        await logError({ type: 'crime', payload: crime, reason: 'Max retries exceeded', timestamp: Date.now() });
        await removeFromStore(STORES?.pendingCrimes, crime?.id);
        failedOps?.push({ type: 'crime', id: crime?.id, payload: crime, reason: 'Max retries exceeded' });
        continue;
      }

      if (crime?.retry_count > 0) {
        await new Promise((r) => setTimeout(r, getBackoffDelay(crime.retry_count - 1)));
      }

      try {
        const { data, error } = await supabase?.rpc('attempt_crime', {
          p_user_id: userId,
          p_crime_key: crime?.crime_key,
        });
        if (error) {
          const errMsg = error?.message?.toLowerCase() || '';
          // Handle "already in jail" — remove from queue, no retry
          if (errMsg?.includes('jail') || errMsg?.includes('jailed')) {
            console.log('[Queue] Crime skipped: player is jailed');
            await removeFromStore(STORES?.pendingCrimes, crime?.id);
            continue;
          }
          // Handle "insufficient energy" — requeue after 5 minutes
          if (errMsg?.includes('energy')) {
            console.log('[Queue] Crime requeued: insufficient energy');
            await updateItemInStore(STORES?.pendingCrimes, crime?.id, {
              retry_after: Date.now() + 5 * 60 * 1000,
            });
            continue;
          }
          throw error;
        }
        await removeFromStore(STORES?.pendingCrimes, crime?.id);
        processed++;
        onProgress?.({ type: 'crime', status: 'success' });
      } catch (err) {
        await updateRetryCount(STORES?.pendingCrimes, crime?.id, crime?.retry_count + 1);
        failed++;
        if (crime?.retry_count + 1 >= MAX_RETRIES) {
          failedOps?.push({ type: 'crime', id: crime?.id, payload: crime, reason: err?.message });
        }
      }
    }
  } catch (err) {
    console.error('[Queue] Crime processing error:', err);
  }

  await emitQueueStatus();
  return { processed, failed, conflicts, failedOps };
}

// ── Connection restore listener ─────────────────────────────────
let onlineHandler = null;

export function setupConnectionRestoreListener({ supabase, userId, onSync }) {
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler);
  }

  onlineHandler = async () => {
    const count = await getPendingCount();
    if (count === 0) return;

    // Exponential backoff before processing
    let attempt = 0;
    const tryProcess = async () => {
      attempt++;
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 30000);
      await new Promise((r) => setTimeout(r, delay));

      const result = await processPendingQueue({ supabase, userId });
      if (result?.processed > 0) {
        onSync?.(`Connection restored - synced ${result?.processed} pending operation${result?.processed !== 1 ? 's' : ''}`);
      }
    };

    tryProcess();
  };

  window.addEventListener('online', onlineHandler);
  return () => window.removeEventListener('online', onlineHandler);
}

export { getAllFromStore };